import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ban as Bank } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { getHypothecationRequests } from '../services/api';
import Swal from 'sweetalert2';
import VahanfinMeta from '../components/VahanfinMeta';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuthStore();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await login(email, password);
      
      // Show loading alert for data fetch
      const loadingAlert = Swal.fire({
        title: 'Loading your data...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      
      // Fetch initial data with JWT token
      try {
        const response = await getHypothecationRequests();
        
        // Close loading alert
        loadingAlert.close();
        
        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Login successful'
          }).then(() => {
            // Only navigate after the Swal dialog is closed
            navigate('/dashboard', { replace: true }); // Use replace to prevent back navigation to login
          });
        } else {
          // Still navigate but warn about data loading issue
          Swal.fire({
            icon: 'warning',
            title: 'Warning',
            text: 'Login successful but unable to load initial data'
          }).then(() => {
            navigate('/dashboard', { replace: true });
          });
        }
      } catch (dataError) {
        console.error('Error loading initial data:', dataError);
        // Continue navigation even if data loading fails
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Login error:', error.message || error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Invalid credentials',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center">
      <VahanfinMeta />
      <div className="bg-white p-8 rounded-lg shadow-md w-96">
        <div className="flex justify-center mb-4">
          <img src="/logo/jk.png" alt="Logo" className="w-full max-w-[200px]" />
        </div>
        <div className="text-center mb-6">
          <h6 className="font-bold whitespace-nowrap">
            Hypothecation Management Portal
          </h6>
        </div>
        
        <form onSubmit={handleLogin}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-2 border rounded"
              required
            />
          </div>
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-2 border rounded"
              required
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:bg-blue-400"
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
      
      {/* Footer with "Powered by" text */}
      <div className="mt-4 text-center">
        <p className="text-xs text-gray-500">
          Powered by <a href="https://www.vahanfin.com/channels/messaging/hypothecation" 
            className="text-blue-600 hover:underline" 
            target="_blank" 
            rel="noopener noreferrer">
            Vahanfin Solutions Pvt Ltd
          </a>
        </p>
      </div>
    </div>
  );
}

export default Login;